/**
 * The organization's spaces in Home's sidebar, with monochrome
 * marks, then an "All spaces" row; the header's plus makes a new space.
 *
 * @module components/home/HomeSidebarSpaces
 */
import { Link, useLocation } from "@tanstack/react-router";
import { LayersIcon, PlusIcon } from "lucide-react";

import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrgId } from "../../state/orgs";
import { useOrgSpaces } from "../../state/spaces";
import { SpaceGlyph } from "../spaces/SpaceGlyph";
import { SidebarMenu, SidebarMenuItem } from "../ui/sidebar";
import { SidebarRow } from "../ui/sidebar-row";
import { SidebarSection } from "../ui/sidebar-section";
import { homePageOf, spaceIdOfPathname } from "./homePaths";
import { NewSpacePopover } from "./HomeSpaces";
import { HomeSidebarOverviewRow } from "./HomeSidebarOverviewRow";
import { useHomeSidebarSectionOpen } from "./HomeSidebarSection";

export function HomeSidebarSpaces({ onNavigate }: { readonly onNavigate: () => void }) {
  const orgId = useActiveOrgId();
  const { available, spaces } = useOrgSpaces(orgId);
  const pathname = useLocation({ select: (location) => location.pathname });
  const [open, setOpen] = useHomeSidebarSectionOpen("spaces");
  if (!available || orgId === null) return null;
  const org = orgUrlSegment(orgId);
  const openSpaceId = spaceIdOfPathname(pathname);
  return (
    <SidebarSection
      label="Spaces"
      open={open}
      onOpenChange={setOpen}
      action={
        <NewSpacePopover
          tip="New space"
          onNavigate={onNavigate}
          render={
            <button
              type="button"
              aria-label="New space"
              data-home-new-space=""
              className="inline-flex size-5 items-center justify-center rounded text-sidebar-muted-foreground/60 hover:bg-sidebar-row-hover hover:text-sidebar-foreground"
            />
          }
        >
          <PlusIcon strokeWidth={1.75} className="size-3.5" />
        </NewSpacePopover>
      }
    >
      <SidebarMenu className="gap-0.5 ps-px">
        {spaces.map((space) => (
          <SidebarMenuItem key={space.id}>
            <SidebarRow
              isActive={openSpaceId === space.id}
              render={<Link to="/$org/spaces/$spaceId" params={{ org, spaceId: space.id }} onClick={onNavigate} />}
            >
              <SpaceGlyph space={space} className="bg-sidebar-foreground/14 text-sidebar-foreground" />
              <span className="min-w-0 flex-1 truncate">{space.name}</span>
            </SidebarRow>
          </SidebarMenuItem>
        ))}
        <HomeSidebarOverviewRow
          to="/$org/spaces"
          org={org}
          icon={<LayersIcon />}
          label="All spaces"
          isActive={homePageOf(pathname) === "spaces"}
          onNavigate={onNavigate}
        />
      </SidebarMenu>
    </SidebarSection>
  );
}
